import React, { useState, useEffect } from "react";
import { useUser } from "util/UserContext";
import api from "config/axiosConfig";
import EntityAvatar from "components/Entities/EntityAvatar";
import LoadingSkeleton from "ui/LoadingSkeleton";
import { getActorDetails } from "util/getObjectDetails";
import { MoreHorizontal, Check, X, UserPlus } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "ui/DropdownMenu";
import { toast } from "sonner";
import { Team } from "util/constants";
import TeamInviteModal from "./TeamInviteModal";
import { apiRoutes } from "util/apiRoutes";

interface TeamMember {
    id: number;
    member_id: number;
    member_type: "user" | "team";
    team_id: number;
    properties: {
        role: "creator" | "moderator" | "member";
        is_active: boolean;
        created_at: string;
        set_at: string;
    };
    is_pending?: boolean;
    member_status?: string;
}

interface Props {
    team: Team;
}

const TeamSettingsMembers: React.FC<Props> = ({ team }) => {
    const { currentUser } = useUser();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [enrichedMembers, setEnrichedMembers] = useState<any[]>([]);
    const [showInviteModal, setShowInviteModal] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get(apiRoutes.team.members(team.id));

                // Enrich the members data
                const enriched = await Promise.all(response.data.map(async (member: TeamMember) => {
                    const enrichedData = await getActorDetails(member.member_type, member.member_id);
                    
                    return {
                        ...member,
                        id: member.member_id,
                        type: member.member_type,
                        member_entity: enrichedData
                    };
                }));

                // Sort members by role: creator > moderator > member > pending
                const sortedMembers = enriched.sort((a, b) => {
                    const roleOrder: Record<string, number> = { 
                        creator: 0, 
                        moderator: 2, 
                        member: 3,
                        pending: 1 
                    };
                    const aRole = a.is_pending ? "pending" : a.properties.role;
                    const bRole = b.is_pending ? "pending" : b.properties.role;
                    return roleOrder[aRole] - roleOrder[bRole];
                });

                setEnrichedMembers(sortedMembers);
            } catch (error) {
                console.error("Error fetching partea members:", error);
                setError("Failed to load partea members.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [team.id]);

    const fetchData = async () => {
        try {
            const response = await api.get(apiRoutes.team.members(team.id));

            // Enrich the members data
            const enriched = await Promise.all(response.data.map(async (member: TeamMember) => {
                const enrichedData = await getActorDetails(member.member_type, member.member_id);
                
                return {
                    ...member,
                    id: member.member_id,
                    type: member.member_type,
                    member_entity: enrichedData
                };
            }));

            // Sort members by role: creator > moderator > member > pending
            const sortedMembers = enriched.sort((a, b) => {
                const roleOrder: Record<string, number> = { 
                    creator: 0, 
                    moderator: 2, 
                    member: 3,
                    pending: 1 
                };
                const aRole = a.is_pending ? "pending" : a.properties.role;
                const bRole = b.is_pending ? "pending" : b.properties.role;
                return roleOrder[aRole] - roleOrder[bRole];
            });

            setEnrichedMembers(sortedMembers);
        } catch (error) {
            console.error("Error fetching partea members:", error);
            setError("Failed to load partea members.");
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMember = async (memberId: number, memberType: string) => {
        try {
            await api.delete(apiRoutes.team.members(team.id), {
                data: {
                    memberId,
                    memberType
                }
            });

            // Remove member from state
            setEnrichedMembers(prev => prev.filter(member => 
                !(member.member_id === memberId && member.member_type === memberType)
            ));

            toast.success("Member removed successfully");
        } catch (error) {
            console.error("Error removing member:", error);
            toast.error("Failed to remove member");
        }
    };

    const handlePromoteToModerator = async (memberId: number, memberType: string) => {
        try {
            await api.patch(apiRoutes.team.memberRole(team.id), {
                memberId,
                memberType,
                role: "moderator"
            });

            // Update member role in state
            setEnrichedMembers(prev => prev.map(member => {
                if (member.member_id === memberId && member.member_type === memberType) {
                    return {
                        ...member,
                        properties: {
                            ...member.properties,
                            role: "moderator"
                        }
                    };
                }
                return member;
            }));

            toast.success("Member promoted to moderator");
        } catch (error) {
            console.error("Error promoting member:", error);
            toast.error("Failed to promote member");
        }
    };

    const handleDemoteToMember = async (memberId: number, memberType: string) => {
        try {
            await api.patch(apiRoutes.team.memberRole(team.id), {
                memberId,
                memberType,
                role: "member"
            });

            // Update member role in state
            setEnrichedMembers(prev => prev.map(member => {
                if (member.member_id === memberId && member.member_type === memberType) {
                    return {
                        ...member,
                        properties: {
                            ...member.properties,
                            role: "member"
                        }
                    };
                }
                return member;
            }));

            toast.success("Removed from moderators");
        } catch (error) {
            console.error("Error removing from moderators:", error);
            toast.error("Failed to remove from moderators");
        }
    };

    const handleRequestAction = async (member: any, accept: boolean) => {
        try {
            await api.patch(apiRoutes.requests.byId(member.properties.id), {
                status: accept ? "accepted" : "rejected"
            });

            if (accept) {
                // Update member status in state
                setEnrichedMembers(prev => prev.map(m => {
                    if (m.member_id === member.member_id && m.member_type === member.member_type) {
                        return {
                            ...m,
                            is_pending: false,
                            properties: {
                                ...m.properties,
                                role: "member",
                                is_active: true
                            }
                        };
                    }
                    return m;
                }));
                toast.success("Member request accepted");
            } else {
                // Remove rejected member from state
                setEnrichedMembers(prev => 
                    prev.filter(m => !(m.member_id === member.member_id && m.member_type === member.member_type))
                );
                toast.success("Member request rejected");
            }
        } catch (error) {
            console.error("Error handling request action:", error);
            toast.error("Failed to process request");
        }
    };

    const handleRequestCancel = async (member: any) => {
        try {
            await api.delete(apiRoutes.requests.cancel(member.properties.id));
            setEnrichedMembers(prev => 
                prev.filter(m => !(m.member_id === member.member_id && m.member_type === member.member_type))
            );
            toast.success(`Cancelled invite to ${member.username}`);
        } catch (error) {
            console.error("Error cancelling request:", error);
            toast.error("Failed to cancel request");
        }
    };

    const MemberActions = ({ member }: { member: any }) => {
        const isCreator = member.properties.is_creator === true;
        const isCurrentUser = member.member_id === currentUser?.id;
        const isTeamModerator = currentUser?.id === team.properties_public?.creator_id || 
            (member.member_id === currentUser?.id && member.properties.role === "moderator");

        if (!isTeamModerator || isCreator || isCurrentUser) return null;

        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="card-more pl-0.5 rounded-full size-5 items-center justify-center">
                        <MoreHorizontal className="icon-stroke items-center justify-center size-4" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    {!member.is_pending && member.properties.role !== "moderator" && (
                        <DropdownMenuItem onClick={() => handlePromoteToModerator(member.member_id, member.member_type)}>
                            Promote to moderator
                        </DropdownMenuItem>
                    )}
                    {!member.is_pending && member.properties.role === "moderator" && (
                        <DropdownMenuItem onClick={() => handleDemoteToMember(member.member_id, member.member_type)}>
                            Remove from moderators
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                        onClick={() => handleRemoveMember(member.member_id, member.member_type)}
                        className="text-warning-red dark:text-warning-red-dark"
                    >
                        Remove from partea
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    };

    const getRoleBadge = (member: any) => {
        const badges = {
            founder: "bg-purple-500/10 text-purple-500 dark:bg-purple-400/10 dark:text-purple-400",
            moderator: "bg-blue-500/10 text-blue-500 dark:bg-blue-400/10 dark:text-blue-400",
            member: "bg-gray-500/10 text-gray-500 dark:bg-gray-400/10 dark:text-gray-400",
            requested: "bg-yellow-500/10 text-yellow-500 dark:bg-yellow-400/10 dark:text-yellow-400",
            invited: "bg-pink-500/10 text-pink-500 dark:bg-pink-400/10 dark:text-pink-400"
        };

        const role = member.is_pending ? member.role : member.properties.is_creator ? "founder" : member.properties.role;
        return (
            <span className={`text-xs px-2 rounded-full ${badges[role as keyof typeof badges]}`}>
                {role}
            </span>
        );
    };

    const getExcludeIds = () => {
        return enrichedMembers
            .filter(member => !member.is_pending)
            .map(member => member.member_id);
    };

    if (loading) {
        return (
            <div className="space-y-2">
                <LoadingSkeleton count={1} height="h-8" width="w-24" className="bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-full" />
                <LoadingSkeleton variant="entity-avatar" rounded="rounded-md" padding="p-2" margin="mx-2" avatarSize="h-9 w-9" count={10} backgroundColor="bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-warning-red dark:text-warning-red-dark text-center p-4">
                {error}
            </div>
        );
    }

    if (enrichedMembers.length === 0) {
        return (
            <div className="text-eoe-text-sub dark:text-eoe-dark-text-sub text-center p-4">
                No members yet
            </div>
        );
    }

    return (
        <div className="">
            <div className="flex justify-between items-center mb-4">
                <button
                    onClick={() => setShowInviteModal(true)}
                    className="btn-primary flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
                >
                    <UserPlus className="size-4" />
                    <span>Invite</span>
                </button>
            </div>
            <div className="space-y-2">
                {enrichedMembers.map((member) => (
                    <div 
                        key={`${member.type}-${member.id}`} 
                        className={`flex items-center justify-between p-2 rounded-md
                            ${member.is_pending 
                                ? "bg-eoe-bg-modal-hover dark:bg-eoe-dark-bg-modal-hover" 
                                : "bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover"}`}
                    >
                            <EntityAvatar
                                entity={member.member_entity}
                                entityType={member.type}
                                entityChip={true}
                                showCloseChip={false}
                                isInputField={false}
                                badgeElement={getRoleBadge(member)}
                                showLink={true}
                            /> 
                        <div className="flex flex-wrap justify-end items-center gap-x-1 gap-y-1.5 flex-grow">
                            {member.is_pending ? 
                                <>
                                    {member.role === "requested" ? 
                                        <>
                                            <button
                                                onClick={() => handleRequestAction(member, true)}
                                                className="btn-primary opacity-75 flex items-center gap-0.5 pr-2 pl-1 py-0.5 rounded-full bg-eoe-error hover:bg-eoe-error-hover text-white text-xs font-normal"
                                            >
                                                <Check className="w-4 h-4" />
                                                Accept
                                            </button>
                                            <button
                                                onClick={() => handleRequestAction(member, false)}
                                                className="btn-secondary opacity-75 flex items-center gap-0.5 pr-2 pl-1 py-0.5 rounded-full text-white text-xs font-normal"
                                            >
                                                <X className="w-4 h-4" />
                                                Decline
                                            </button> 
                                        </> 
                                        : 
                                        <button
                                            onClick={() => handleRequestCancel(member)}
                                            className="btn-secondary opacity-75 flex items-center gap-0.5 pr-2 pl-1 py-0.5 rounded-full text-white text-xs font-normal"
                                        >
                                            <X className="w-4 h-4" />
                                            Cancel
                                        </button>
                                    }
                                </> 
                                : 
                                <MemberActions member={member} />
                            }
                        </div>
                    </div>
                ))}
            </div>
            {showInviteModal && (
                <TeamInviteModal
                    team={team}
                    onClose={() => setShowInviteModal(false)}
                    excludeIds={getExcludeIds()}
                    onInviteSuccess={() => {
                        // Refresh the members list
                        fetchData();
                    }}
                />
            )}
        </div>
    );
};

export default TeamSettingsMembers; 